import { Avatar, AvatarGroup } from "@dash/avatar";
import { useDialogRoute } from "@dash/dialog";
import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { SectionHeader } from "@dash/page";
import { SearchButton } from "@dash/search";
import { DocsSelectDialog, AnyDocSelectOption } from "@dash/select";
import { useToast } from "@dash/toast";
import { useTheme } from "@emotion/react";
import { useProfiles } from "@megarax/features";
import { useCallback, useContext } from "react";
import { MdOutlineGroupAdd, MdOutlinePersonAddAlt } from "react-icons/md";
import { useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { ThreadDto } from "@megaron/invoices-contracts";
import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { ResourceId } from "@megaron/resource";

type Props = {
  thread: ThreadDto;
  queryKey: string | string[];
};

export const ParticipantsSection: React.FC<Props> = (props) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const auth = useContext(IamAuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const { profiles } = useProfiles();
  const { isDesktop } = useDeviceType();
  const toast = useToast();

  const sortedParticipants = props.thread.participants
    .sort((a, b) => {
      const dateA = new Date(a.invitedAt).getTime();
      const dateB = new Date(b.invitedAt).getTime();
      return dateA - dateB;
    })
    .map((participant) => participant.user);

  const inviteParticipantsMutation = useClientManager("invoices").inviteParticipants().useMutation();
  const authClient = useServiceClient("auth");

  const inviteParticipantsFromGroups = useCallback(
    async (groupIds: string[]) => {
      const allEmails: string[] = [];
      for (const id of groupIds) {
        const result = await authClient.groupQuery({ id });
        if (result.isOk) {
          for (const a of result.value.accounts) {
            if (a.email) allEmails.push(a.email);
          }
        }
      }
      const toInvite = [...new Set(allEmails)].filter(
        (email) => !sortedParticipants.includes(email),
      );
      if (toInvite.length === 0) return;
      try {
        await inviteParticipantsMutation.mutateAsync({
          threadUuid: props.thread.uuid,
          users: toInvite,
        });
        queryClient.invalidateQueries(props.queryKey);
      } catch {
        toast.error("Error", "Coś poszło nie tak");
      }
    },
    [
      authClient,
      sortedParticipants,
      inviteParticipantsMutation,
      props.thread.uuid,
      props.queryKey,
      queryClient,
      toast,
    ],
  );

  const emailOrderMap = new Map(sortedParticipants.map((email, index) => [email, index]));

  const participantsData = profiles(sortedParticipants) ?? [];

  const userEmail = auth.iamUser?.userType === "megarax" ? auth.iamUser.email : undefined;
  const isParticipant = userEmail && props.thread.participants.map((p) => p.user).includes(userEmail);

  const sortedParticipantsData = participantsData.sort((a, b) => {
    const indexA = emailOrderMap.get(a.email) ?? -1;
    const indexB = emailOrderMap.get(b.email) ?? -1;
    return indexA - indexB;
  });

  const inviteEmails = (emails: string[]) => {
    if (emails.length === 0) return;
    inviteParticipantsMutation.mutate(
      { threadUuid: props.thread.uuid, users: emails },
      {
        onSuccess: () => queryClient.invalidateQueries(props.queryKey),
        onError: () => toast.error("Error", "Coś poszło nie tak"),
      },
    );
  };

  const addParticipantFromGroupDialog = useDialogRoute("/zapros-stanowisko", ({ onClose }) => (
    <DocsSelectDialog
      docType={["user-group"]}
      variant="single-select"
      label="Wybierz stanowisko"
      optionalHeaderContent={
        <div css={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
          <button
          type="button"
          onClick={() => {
            const base = location.pathname.replace(/\/zapros-stanowisko$/, "");
            navigate(base + "/zapros-uzytkownikow" + location.search);
          }}
          css={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: "none",
            border: "none",
            padding: 0,
            color: "white",
            fontSize: "1rem",
            cursor: "pointer",
          }}
        >
          <MdOutlinePersonAddAlt size={20} />
          Zaproś użytkowników
        </button>
        </div>
      }
      onSelect={async (option) => {
        if (!option) return;
        await inviteParticipantsFromGroups([option.value]);
        onClose();
      }}
      isOpen
      onClose={onClose}
    />
  ));

  const addParticipantDialog = useDialogRoute("/zapros-uzytkownikow", ({ onClose }) => (
    <DocsSelectDialog
      docType={["user"]}
      initiallySelectedDocIds={participantsData.map((participant) => ResourceId("user", participant.email))}
      variant="multi-select"
      label="Zaproś użytkowników"
      onSelectedChange={(options: AnyDocSelectOption[]) => {
        const emails = options.map((option) => option.value);
        inviteEmails(emails);
      }}
      isUnselectDisabled
      isOpen
      onClose={onClose}
      noTrigger
      optionalHeaderRightIcon={
        <Button
          variant="text"
          css={{
            color: "white",
            fontSize: "14px",
            minWidth: "auto",
            padding: "4px 8px",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
          icon={<MdOutlineGroupAdd size={18} />}
          onClick={() => {
            const base = location.pathname.replace(/\/zapros-uzytkownikow$/, "");
            navigate(base + "/zapros-stanowisko" + location.search);
          }}
        >
          zaproś stanowisko
        </Button>
      }
    />
  ));

  return (
    <>
      {isDesktop && (
        <SectionHeader
          isHr
          css={{ h2: { fontSize: "16px" } }}
          actions={
            !isParticipant &&
            userEmail && (
              <Button
                css={{ fontSize: "14px" }}
                icon={<MdOutlinePersonAddAlt />}
                variant="outline"
                isLoading={inviteParticipantsMutation.isLoading}
                onClick={() => inviteEmails([userEmail])}
              >
                Dołącz
              </Button>
            )
          }
        >
          Uczestnicy
        </SectionHeader>
      )}
      {!isDesktop ? (
        <div css={{ display: "flex", gap: "8px", alignItems: "center", paddingLeft: "8px" }}>
          <AvatarGroup profiles={sortedParticipantsData} />
          <button
            onClick={addParticipantDialog.open}
            css={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "100%",
              border: "none",
              padding: "0.25rem",
              width: "30px",
              height: "30px",
              background: theme.colors.primaryLight,
              flexShrink: 0,
            }}
          >
            <MdOutlineGroupAdd size={16} css={{ color: theme.colors.primary }} />
          </button>

          {addParticipantDialog.element}
          {addParticipantFromGroupDialog.element}
        </div>
      ) : (
        <div css={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div css={{ display: "flex", flexDirection: "column", padding: "0 8px", gap: "10px" }}>
            {sortedParticipantsData.map((profile) => (
              <div css={{ display: "flex", gap: "10px", alignItems: "center" }} key={profile.email}>
                <Avatar src={profile.profilePictureUrl} />
                <span css={{ fontSize: "14px", fontWeight: 400 }}>
                  {profile.firstName} {profile.lastName}
                </span>
              </div>
            ))}
          </div>

          <SearchButton variant="pale" onClick={addParticipantDialog.open} inputText="Zaproś użytkowników..." />

          {addParticipantDialog.element}
          {addParticipantFromGroupDialog.element}
        </div>
      )}
    </>
  );
};
